// web/src/admin/AdminCharts.tsx — the two charts of 50_ADMIN_CONSOLE.md §3.1, as INLINE SVG.
//
// WHY NO CHARTING LIBRARY (50 §6 says to check first, so this is the check, written down):
// `web/package.json` has none, and `grep -ri chart|recharts|<svg` across `web/src` returns exactly
// one file — `Icon.tsx`, which hand-rolls its SVG paths for the same reason. Two stacked bar
// charts do not justify adding Recharts (~100 kB gzipped, its own theming model, a second place
// colour is decided) to a phone-first SPA whose entire dependency list is eleven packages.
//
// THEMING (docs/THEMING.md §2): every colour is a semantic Tailwind class bound to a CSS variable
// — `fill-info`, `fill-run`, `text-muted`. Not one hex appears below, so both charts re-theme
// across the 2 × 6 mode/accent matrix for free. An SVG `fill` set from a hex would be the one
// thing on the page that ignores the accent picker.
//
// RESPONSIVE: fixed viewBox, `w-full h-auto`. The bars are laid out in viewBox units and the
// browser scales them, so there is no measuring, no ResizeObserver, and nothing to overflow at
// 390 px — which is the §7 item 11 requirement.
import type { AdminOutcomeDay, AdminSpendDay } from '../lib/types';

const money = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

/** Token-bound series colours, in assignment order. Semantic classes only — never a hex. */
const SERIES = ['fill-run', 'fill-info', 'fill-accent', 'fill-success', 'fill-warn', 'fill-muted'];
const LEGEND_DOT = ['bg-run', 'bg-info', 'bg-accent', 'bg-success', 'bg-warn', 'bg-muted'];

const VB_W = 720;
const VB_H = 160;

function Frame({ children, empty }: { children: React.ReactNode; empty: boolean }) {
  if (empty) {
    return (
      <p className="flex h-[120px] items-center justify-center rounded-md border border-dashed border-hair text-xs text-muted">
        No data in this window.
      </p>
    );
  }
  return (
    <svg viewBox={`0 0 ${VB_W} ${VB_H}`} role="img" className="h-auto w-full" preserveAspectRatio="none">
      {children}
    </svg>
  );
}

function Legend({ items }: { items: { label: string; i: number }[] }) {
  return (
    <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
      {items.map(({ label, i }) => (
        <li key={label} className="flex items-center gap-1.5 text-[11px] text-muted">
          <span className={`size-2 shrink-0 rounded-sm ${LEGEND_DOT[i % LEGEND_DOT.length]}`} />
          {label}
        </li>
      ))}
    </ul>
  );
}

/**
 * Daily AI spend, stacked by DEPLOYMENT name (§3.1 chart 1).
 *
 * The split is the point, not the total: 02 §3 makes the complexity classifier "the primary cost
 * lever in the system", so a growing Opus band is the earliest visible sign of classifier drift —
 * earlier than the complex-mix tile, because a few expensive requests move dollars before they
 * move a percentage.
 */
export function SpendByModelChart({ days }: { days: AdminSpendDay[] }) {
  const models = [...new Set(days.flatMap((d) => Object.keys(d.byModel)))].sort();
  const peak = Math.max(1, ...days.map((d) => d.totalCents));
  const slot = VB_W / Math.max(days.length, 1);
  const barW = Math.max(1, slot * 0.72);

  return (
    <div>
      <Frame empty={days.length === 0}>
        {days.map((d, x) => {
          let y = VB_H;
          return (
            <g key={d.day}>
              <title>{`${d.day} — ${money(d.totalCents)}`}</title>
              {models.map((m, i) => {
                const cents = d.byModel[m] ?? 0;
                if (!cents) return null;
                const h = (cents / peak) * (VB_H - 4);
                y -= h;
                return (
                  <rect
                    key={m}
                    x={x * slot + (slot - barW) / 2}
                    y={y}
                    width={barW}
                    height={h}
                    className={SERIES[i % SERIES.length]}
                  />
                );
              })}
            </g>
          );
        })}
      </Frame>
      <div className="mt-1 flex items-center justify-between text-[11px] text-muted">
        <span>{days[0]?.day ?? ''}</span>
        <span>peak {money(peak)}/day</span>
        <span>{days[days.length - 1]?.day ?? ''}</span>
      </div>
      <Legend items={models.map((label, i) => ({ label, i }))} />
    </div>
  );
}

/**
 * Requests by outcome (§3.1 chart 2): live / failed / needs_attention, stacked per day.
 *
 * `needs_attention` gets its own band rather than being folded into "other" because it is the one
 * outcome that is still blocking a customer right now (D-14) — a day with a tall amber band is a
 * day someone is waiting on KC, not a day that merely went badly.
 */
export function OutcomeChart({ days }: { days: AdminOutcomeDay[] }) {
  const bands: { key: keyof Omit<AdminOutcomeDay, 'day'>; label: string; fill: string; dot: string }[] = [
    { key: 'live', label: 'Live', fill: 'fill-success', dot: 'bg-success' },
    { key: 'needsAttention', label: 'Needs attention', fill: 'fill-warn', dot: 'bg-warn' },
    { key: 'failed', label: 'Failed', fill: 'fill-danger', dot: 'bg-danger' },
    { key: 'other', label: 'In flight', fill: 'fill-muted', dot: 'bg-muted' },
  ];
  const peak = Math.max(1, ...days.map((d) => d.live + d.failed + d.needsAttention + d.other));
  const slot = VB_W / Math.max(days.length, 1);
  const barW = Math.max(1, slot * 0.72);

  return (
    <div>
      <Frame empty={days.length === 0}>
        {days.map((d, x) => {
          let y = VB_H;
          return (
            <g key={d.day}>
              <title>{`${d.day} — ${d.live} live, ${d.needsAttention} held, ${d.failed} failed`}</title>
              {bands.map((b) => {
                const n = d[b.key];
                if (!n) return null;
                const h = (n / peak) * (VB_H - 4);
                y -= h;
                return (
                  <rect
                    key={b.key}
                    x={x * slot + (slot - barW) / 2}
                    y={y}
                    width={barW}
                    height={h}
                    className={b.fill}
                  />
                );
              })}
            </g>
          );
        })}
      </Frame>
      <div className="mt-1 flex items-center justify-between text-[11px] text-muted">
        <span>{days[0]?.day ?? ''}</span>
        <span>peak {peak}/day</span>
        <span>{days[days.length - 1]?.day ?? ''}</span>
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {bands.map((b) => (
          <li key={b.key} className="flex items-center gap-1.5 text-[11px] text-muted">
            <span className={`size-2 shrink-0 rounded-sm ${b.dot}`} />
            {b.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
